import {Component, EventEmitter, Input, Output, SimpleChanges, ViewChild} from '@angular/core';
import {SelectItem} from 'primeng/api';
import {Dropdown} from 'primeng/dropdown';
import {SelectableLab} from '../carrier/selectable-lab';
import {ButtonType} from '../constants/ButtonType';
import {PlateScanType} from '../constants/PlateScanType';
import {Project} from '../masterdata/models/project';
import {ProjectWorkflow} from '../masterdata/models/projectWorkflow';
import {MessagesNewService} from '../messages-new/messages.service';
import {Plate} from '../models-new/plate';
import {ProcessStep} from '../models-new/processStep';
import {OpenDialogDTO} from '../multiplate-update/open-dialog-dto';
import {ConfigurationService} from '../protocol-configuration/configuration.service';
import {ActionComponent} from '../protocol-configuration/dto/ActionComponent';
import {PopUpComponent} from '../protocol-configuration/dto/PopUpComponent';
import {Protocol, ProtocolMeta, ProtocolType} from '../protocol-configuration/dto/Protocol';
import {ProtocolComponent} from '../protocol-configuration/dto/ProtocolComponent';
import {PredefinedRedoConfiguration} from '../protocol-configuration/dto/RedoConfiguration';
import {MultiQcUpdateDTO, QCFailPlateDetailsResponse, RedoPlateInformation} from './multiplate-qc.dto';
import {MultiplateQcService} from './multiplate-qc.service';
import {SampleRedoDTO, SampleWithAnalysisType} from '../models/sampleRedoDTO';
import {AnalysisType} from '../models/analysis-type';
import { BeadChipStatus } from '../models/process-step';
import { ErrorHandler } from '../utils/error-handler';
import { HistoryDialogDTO } from '../plate-search/history-dialog-dto';

const PLATE_NOT_ELIGIBLE = "Plate not eligible!";
@Component({
	selector: 'app-multiplate-qc',
	templateUrl: './multiplate-qc.component.html'
})
export class MultiplateQCComponent {
	@Input() selectedProtocol: Protocol;
	@Input() openDialogDTO: OpenDialogDTO;
	@Input() projectMap: Map<string, Project> = new Map();
	@Input() workflowMap: Map<string, ProjectWorkflow> = new Map();
	@Input() selectedProjectWorkflow: ProjectWorkflow;
	@Input() selectedProject: Project;
	@Input() historyDialogDTO: HistoryDialogDTO;

	@Output() platesUpdatedEmitter = new EventEmitter<any>();
	@Output() dialogHeaderEmitter = new EventEmitter<any>();
	@Output() closeDialogEmitter = new EventEmitter<any>();

	blocked = false;
	selectedLab: SelectableLab;
	puid: string;
	dialogHeader = "";
	popUpComponent: PopUpComponent = new PopUpComponent();
	popUpProtocolComponentName!: string;
	popUpProtocolComponentIndex = 0;
	popUpAction!: ActionComponent;
	popUpActionIndex = -1;
	popUpUpdateType!: string;
	popUpScanInputValues: Array<string> = [];
	popUpScanInputSourcePlateID = "";
	redoSelectionMap = {};
	upstreamProtocol: Protocol;
	downstreamProtocol: Protocol;

	allRedoConfigMap: any = {};
	redoOptions: SelectItem[] = [];
	selectedRedoType = "";
	errorHandler: ErrorHandler = new ErrorHandler;
	
	mappedRedoPlates: Array<RedoPlateInformation> = [];
	scannedPlatesMap: any = {};
	associatedArrays: any = {};
	isRedoFromHistory = false;

	@ViewChild('redoSelectorDropdown')
	redoSelectorDropdown: Dropdown;

	private readonly PLATE_NOT_AVAILABLE = "Plate ID not available";

	constructor(private readonly messagesService: MessagesNewService,
		private readonly multiplateQcService: MultiplateQcService,
		private readonly configurationService: ConfigurationService) { }

	ngOnInit(){
		this.selectedLab = JSON.parse(localStorage.getItem('lab') || '{"lab":""}');
		this.puid = localStorage.getItem('PUID') || "PUID";
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes["openDialogDTO"]) {
			this.setUpComponentForUpdates(this.openDialogDTO);
		} else if (changes["historyDialogDTO"]) {
			this.setUpComponentForHistoryRedo(this.historyDialogDTO);
		}
	}

	private setUpComponentForHistoryRedo(historyDialogDTO: HistoryDialogDTO) {
		if (!historyDialogDTO) {
			this.clearAllPopUpValues();
			return;
		}

		this.dialogHeader = historyDialogDTO.dialogName;
		this.popUpUpdateType = ButtonType.ACTION;
		this.isRedoFromHistory = historyDialogDTO.isRedoFromHistory;
		this.popUpAction.name = "PlateRedo";
		this.popUpComponent.popUpTitle = historyDialogDTO.componentPopupTitle;
		this.popUpProtocolComponentName = historyDialogDTO.protocolStepName;
		this.popUpComponent.scans = historyDialogDTO.scans;
		this.popUpComponent.availableRedoList = historyDialogDTO.availableRedoList;
		this.setUpstreamAndDownstreamProtocols();
		this.validateScan(0, this.historyDialogDTO.scannedPlateID, "SOURCE");
	}

	private setUpComponentForUpdates(openDialogDTO: OpenDialogDTO) {
		if (!openDialogDTO) {
			this.clearAllPopUpValues();
			return;
		}

		if(!openDialogDTO.qcFail){
			return;
		}

		const protocolComponent: ProtocolComponent = this.selectedProtocol[openDialogDTO.stepMapName][openDialogDTO.protocolStepName];
		this.popUpProtocolComponentName = openDialogDTO.protocolStepName;
		this.popUpProtocolComponentIndex = this.selectedProtocol[openDialogDTO.stepListName].indexOf(this.popUpProtocolComponentName);
		this.popUpUpdateType = openDialogDTO.buttonType;

		if (ButtonType.ACTION !== openDialogDTO.buttonType) {
			return;
		}
		this.popUpAction = protocolComponent.actionMap[openDialogDTO.actionName]
		this.popUpActionIndex = openDialogDTO.actionIndex;

		this.dialogHeader = this.popUpAction.name + " Fail";
		this.dialogHeaderEmitter.emit(this.dialogHeader);
		this.popUpComponent = this.popUpAction.popUp;
		this.popUpScanInputValues = new Array(this.popUpComponent.scans.length);

		this.setUpstreamAndDownstreamProtocols();

		setTimeout(() => {
			this.findAndHighlightElement("popUpDialog-scan-0")
		}, 300)
	}

	private setUpstreamAndDownstreamProtocols() {
		if(!this.selectedProjectWorkflow.workflowName){
			return;
		}

		this.upstreamProtocol = new Protocol();
		this.downstreamProtocol = new Protocol();
		if (this.selectedProtocol.protocolType === ProtocolType.EXTRACTION) {
			this.upstreamProtocol = this.selectedProtocol;
			this.prepareRedoMap();
			this.prepareRedoDropdownOptions();
		} else {
			this.downstreamProtocol = this.selectedProtocol;
			this.setUpstreamProtocol();
		}
	}

	private prepareRedoDropdownOptions() {
		if(!this.selectedProjectWorkflow.workflowName){
			return;
		}

		this.redoOptions = [];
		this.redoOptions.push({ "label": PredefinedRedoConfiguration.NO_REDO.redoConfigurationName, "value": PredefinedRedoConfiguration.NO_REDO.redoConfigurationName });
		this.redoOptions.push({ "label": PredefinedRedoConfiguration.RECEPTION_REDO.redoConfigurationName, "value": PredefinedRedoConfiguration.RECEPTION_REDO.redoConfigurationName });
		this.redoOptions.push({ "label": PredefinedRedoConfiguration.QC_PASSED.redoConfigurationName, "value": PredefinedRedoConfiguration.QC_PASSED.redoConfigurationName });
		if(this.selectedProtocol.protocolType === ProtocolType.PLATE_ARRAY){
			Object.keys(this.upstreamProtocol.redoConfigurationMap).forEach(key => {
				this.redoOptions.push({ "label": key, "value": key });
			});
		}
		this.popUpComponent.availableRedoList.forEach(redoName => {
			this.redoOptions.push({ "label": redoName, "value": redoName });
		});
	}

	private prepareRedoMap() {
		if(!this.selectedProjectWorkflow.workflowName){
			return;
		}

		this.allRedoConfigMap[PredefinedRedoConfiguration.NO_REDO.redoConfigurationName] = PredefinedRedoConfiguration.NO_REDO;
		this.allRedoConfigMap[PredefinedRedoConfiguration.RECEPTION_REDO.redoConfigurationName] = PredefinedRedoConfiguration.RECEPTION_REDO;
		this.allRedoConfigMap[PredefinedRedoConfiguration.QC_PASSED.redoConfigurationName] = PredefinedRedoConfiguration.QC_PASSED;
		Object.keys(this.upstreamProtocol.redoConfigurationMap).forEach(key => {
			this.setChangeToStatusForReceptionOrQCCompleted(key, this.upstreamProtocol);
			this.allRedoConfigMap[key] = this.upstreamProtocol.redoConfigurationMap[key];

		});
		Object.keys(this.downstreamProtocol.redoConfigurationMap).forEach(key => {
			this.setChangeToStatusForReceptionOrQCCompleted(key, this.downstreamProtocol);
			this.allRedoConfigMap[key] = this.downstreamProtocol.redoConfigurationMap[key];
		});
	}

	private setChangeToStatusForReceptionOrQCCompleted(key: string, protocol: Protocol) {
		const redoConfig = protocol.redoConfigurationMap[key];
		if (redoConfig['changeToStatus'] === "RECEPTION_COMPLETED/QCSCAN_COMPLETED") {
			if (this.selectedProjectWorkflow.qcScan) {
				redoConfig['changeToStep'] = 'QC_SCAN';
				redoConfig['changeToStatus'] = 'QCSCAN_COMPLETED'
			} else {
				redoConfig['changeToStep'] = 'RECEIVE_PLATE';
				redoConfig['changeToStatus'] = 'RECEPTION_COMPLETED'
			}
		}
	}

	setUpstreamProtocol(){
		const upstreamProtocol: ProtocolMeta = this.selectedProjectWorkflow.upstream[0];
		
		if(!upstreamProtocol){
			this.prepareRedoMap();
			this.prepareRedoDropdownOptions();
			return;
		}

		this.blocked = true;
		this.configurationService.getProtocol(this.selectedLab.lab._id, upstreamProtocol.protocolName, upstreamProtocol.protocolVersion).subscribe({
			next: result => {
				this.blocked = false;
				if(JSON.stringify(result) === "{}"){
					this.messagesService.addErrorMessage({
						severity:"error", summary:"Upstream protocol not found",
						detail: `Protocol ${upstreamProtocol.protocolName}(v${upstreamProtocol.protocolVersion}) not found for workflow ${this.selectedProjectWorkflow.workflowName}`
					})
					return;
				}
				this.upstreamProtocol = result.data;
				this.prepareRedoMap();
				this.prepareRedoDropdownOptions();
			},
			error: error => {
				this.blocked = false;
				this.upstreamProtocol = new Protocol();
				this.messagesService.addErrorMessage({
					severity: 'error', summary: 'Error Occured!',
					detail: 'Error occured while fetching protocol config! ' + error.error.bbApiError.message
				})
			}
		})
	}

	findAndHighlightElement(elementId: string) {
		const element = document.getElementById(elementId);
		if (element) {
			element.focus();
		}
	}

	clearAllPopUpValues() {
		this.popUpScanInputValues = [];
		this.mappedRedoPlates = [];
		this.scannedPlatesMap = {};
		this.associatedArrays = {};
		this.redoSelectionMap = {};
		this.popUpComponent = new PopUpComponent();
		this.allRedoConfigMap = {};
		this.redoOptions = [];

		this.dialogHeader = "";
		this.popUpProtocolComponentName = "";
		this.popUpProtocolComponentIndex = -1;
		this.popUpAction = new ActionComponent();
		this.popUpActionIndex = -1;
		this.popUpUpdateType = "";
		this.selectedProject = new Project();
		this.selectedProjectWorkflow = new ProjectWorkflow();
	}

	updateMultiplePlates() {
		if (!this.validateDataForUpdate()) {
			this.messagesService.addWarningMessage({
				severity:'warn', summary: 'Incomplete data',
				detail: "Please select redo for all plates and try again."
			})
			return;
		}

		const multiQcUpdateDTO = this.createDTOForMultiPlateQC();
		this.blocked = true;
		this.multiplateQcService.genericMultiPlateUpdate(multiQcUpdateDTO).subscribe({
			next: result => {
				this.blocked = false;
				this.messagesService.addSuccessMessage({
					severity:'success', summary:`${this.popUpAction.name} completed!`,
					detail: result.data
				})
				const redoSampleObjectIds = this.extractReceptionRedoSampleObjectIds();
				this.updateSamplesForReceptionRedoPlates(redoSampleObjectIds);
			},
			error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity:'error', summary:`${this.popUpAction.name} failed!`,
					detail: error.error.bbApiError.message
				})
			}
		})
	}

	private createDTOForMultiPlateQC() {
		const multiQcUpdateDTO = new MultiQcUpdateDTO();
		multiQcUpdateDTO.puid = this.puid;
		multiQcUpdateDTO.labID = this.selectedLab.lab._id;
		multiQcUpdateDTO.redoConfigurations = this.allRedoConfigMap;
		multiQcUpdateDTO.plateRedoSelections = this.redoSelectionMap;
		multiQcUpdateDTO.scannedPlates = this.scannedPlatesMap;
		multiQcUpdateDTO.mappedRedoPlates = this.mappedRedoPlates;
		multiQcUpdateDTO.associatedArrays = this.associatedArrays;
		multiQcUpdateDTO.processStepName = this.popUpProtocolComponentName;
		multiQcUpdateDTO.actionName = this.popUpAction.name;
		return multiQcUpdateDTO;
	}

	extractReceptionRedoSampleObjectIds(){
		const sampleObjectIds = [];
		Object.keys(this.scannedPlatesMap).forEach( scannedPlateID => {
			const scannedPlate:Plate = this.scannedPlatesMap[scannedPlateID]

			if(scannedPlate.sourcePlates.length > 0){
				const redoSourcePlates = new Set();
				scannedPlate.sourcePlates.forEach(sourcePlate => {
					if(this.redoSelectionMap[sourcePlate.lastUpdatedPlateID] === PredefinedRedoConfiguration.RECEPTION_REDO.redoConfigurationName){
						redoSourcePlates.add(sourcePlate.plateID);
					}
				})
				scannedPlate.samples.forEach(sample => {
					if(redoSourcePlates.has(sample.sourcePlateID))
					{
						sampleObjectIds.push(sample._id)
					}
				})
			}else{
				if(this.redoSelectionMap[scannedPlateID] === PredefinedRedoConfiguration.RECEPTION_REDO.redoConfigurationName){
					scannedPlate.samples.forEach(sample => {
						sampleObjectIds.push(sample._id)
					})
				}
			}
		})
		return sampleObjectIds;
	}

	updateSamplesForReceptionRedoPlates(redoSampleObjectIds: Array<string>){
		if(redoSampleObjectIds.length <= 0){
			this.platesUpdatedEmitter.emit(true);
			this.closeDialogEmitter.emit(true);
			this.clearAllPopUpValues()
			return;
		}

		const sampleRedoDTO: SampleRedoDTO = this.createSampleRedoDTO(redoSampleObjectIds);
		this.blocked = true;
		this.multiplateQcService.updateRedoSample(this.selectedLab.lab._id, sampleRedoDTO).subscribe({
			next: result => {
				this.blocked = false;
				this.messagesService.addSuccessMessage({
					severity:"success", summary: "Redo Samples Updated",
					detail: result.data
				});
				this.platesUpdatedEmitter.emit(true);
				this.closeDialogEmitter.emit(true);
				this.clearAllPopUpValues()
			},
			error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity:"error", summary:"Redo Samples update failed.",
					detail: error.error.bbApiError.message
				})
			}
		})
	}

	private createSampleRedoDTO(redoSampleObjectIds: string[]) {
		const sampleRedoDTO: SampleRedoDTO = new SampleRedoDTO();
		sampleRedoDTO.analysisType = true;
		sampleRedoDTO.reRunCount = false;
		sampleRedoDTO.sampleIdList = redoSampleObjectIds.map(sample => {
			const sampleWithAnalysisType = new SampleWithAnalysisType();
			sampleWithAnalysisType.sampleID = sample;
			sampleWithAnalysisType.type = AnalysisType.INTERNAL_RERUN;
			return sampleWithAnalysisType;
		});
		return sampleRedoDTO;
	}

	validateDataForUpdate(): boolean {
		let isValid = true;
		Object.values(this.redoSelectionMap).forEach(redoName => {
			if(redoName === ""){
				isValid = false;
			}
		})

		return isValid;
	}

	deleteScanValues(index: number, redoPlateInformation: RedoPlateInformation) {
		this.mappedRedoPlates.splice(index, 1);
		delete this.scannedPlatesMap[redoPlateInformation.currentPlateID]
		if (this.associatedArrays[redoPlateInformation.currentPlateID]) {
			delete this.associatedArrays[redoPlateInformation.currentPlateID];
		}
		delete this.redoSelectionMap[redoPlateInformation.currentPlateID];
		if (redoPlateInformation.sourcePlateInformations.length > 0) {
			redoPlateInformation.sourcePlateInformations.forEach(redoSourcePlateInformation => {
				delete this.scannedPlatesMap[redoSourcePlateInformation.sourcePlateID]
				delete this.redoSelectionMap[redoSourcePlateInformation.sourcePlateID];
				if (redoSourcePlateInformation.sourcePlates.length > 0) {
					redoSourcePlateInformation.sourcePlates.forEach(sourcePlate => delete this.redoSelectionMap[sourcePlate]);
				}
			})
		}
	}

	validateScan(scanIndex: number, enteredValue: string, plateType: string) {
		if (scanIndex !== 0 && (!this.popUpScanInputValues[scanIndex - 1] || this.popUpScanInputValues[scanIndex - 1] === "")) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: "Field empty!",
				detail: "Please give some value in the previous field."
			})
			this.findAndHighlightElement(`popUpDialog-scan-${scanIndex - 1}`)
			this.popUpScanInputValues[scanIndex - 1] = "";
			return;
		}

		enteredValue = enteredValue.trim();
		if (enteredValue === "") {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: "Field empty!",
				detail: "Please give some value in the field."
			})
			this.popUpScanInputValues[scanIndex] = "";
			return;
		}

		if (plateType === PlateScanType.WGA && !enteredValue.endsWith("-WGA")) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: "Wrong Suffix",
				detail: "Please scan a WGA plate that ends with -WGA"
			})
			this.popUpScanInputValues[scanIndex] = "";
			return;
		}

		if (plateType === PlateScanType.PGX && !enteredValue.endsWith("-PGX")) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: "Wrong Suffix",
				detail: "Please scan a PGX plate that ends with -PGX"
			})
			this.popUpScanInputValues[scanIndex] = "";
			return;
		}

		this.validatePlateBarcode(scanIndex, plateType, enteredValue);
	}

	showWarningMessageAndClearField(scanIndex: number, summary: string, detail: string) {
		this.messagesService.addWarningMessage({
			severity: "warn", summary: summary, detail: detail
		})
		this.popUpScanInputValues[scanIndex] = "";
	}

	validatePlateBarcode(scanIndex: number, plateType: string, plateBarcode: string) {
		for (const mappedRedoPlate of this.mappedRedoPlates) {
			if (mappedRedoPlate.currentPlateID === plateBarcode) {
				this.messagesService.addWarningMessage({
					severity: 'warn', 
					summary: "Duplicate Plate ID Scanned!",
					detail: `Plate: ${plateBarcode} is already Scanned, Please Scan another plateID.`
				});
				this.popUpScanInputValues[scanIndex] = "";
				return;
			}
		
			for (const sourcePlateInfo of mappedRedoPlate.sourcePlateInformations) {
				if (sourcePlateInfo.sourcePlateID === plateBarcode) {
					this.messagesService.addWarningMessage({
						severity: 'warn', 
						summary: "Duplicate Plate ID Scanned!",
						detail: `Scanned Plate: ${plateBarcode} is an associated plate of ${mappedRedoPlate.currentPlateID}, Please Scan another plateID.`
					});
					this.popUpScanInputValues[scanIndex] = "";
					return;
				}
			}
		}

		this.fetchAndCheckIfPlateIsEligible(scanIndex, plateType, plateBarcode);
	}

	prepareMapOfPlateProcessSteps(plate: Plate): Map<string, ProcessStep> {
		const plateStepsMap: Map<string, ProcessStep> = new Map();
		if (!plate.processSteps || plate.processSteps === null) {
			return plateStepsMap;
		}

		plate.processSteps.forEach(step => {
			step.actionMap = new Map();
			step.action.forEach(s => {
				step.actionMap.set(s.action, s);
			})
			plateStepsMap.set(step.step, step);
		});

		return plateStepsMap;
	}

	validateProjectAndWorkflow(fetchedPlate: Plate){
		if(!this.selectedProject.projectId){
			this.selectedProject = this.projectMap.get(fetchedPlate.projectID) || new Project();
		}
		if(this.selectedProject.projectId !== fetchedPlate.projectID){
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: PLATE_NOT_ELIGIBLE,
				detail: `Plate project '${fetchedPlate.projectName}(${fetchedPlate.projectID})' does not match the current selected project '${this.selectedProject.projectName}(${this.selectedProject.projectId})`
			})
			return false;
		}

		// Check for condition when project is same and workflows are different
		if(!this.selectedProjectWorkflow.workflowName){
			this.selectedProjectWorkflow = this.workflowMap.get(`${fetchedPlate.projectID}_${fetchedPlate.workFlow}`) || new ProjectWorkflow();
			this.setUpstreamAndDownstreamProtocols();
		}
		if (this.selectedProjectWorkflow.workflowName !== fetchedPlate.workFlow){
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: PLATE_NOT_ELIGIBLE,
				detail: `Plate workflow '${fetchedPlate.workFlow}' does not match the current selected workflow '${this.selectedProjectWorkflow.workflowName}'`
			})
			return false;
		}

		return true
	}

	fetchAndCheckIfPlateIsEligible(scanIndex: number, plateType: string, plateBarcode: string) {
		this.blocked = true;
		this.multiplateQcService.getQCFailPlateDetailsForMultiUpdate(this.selectedLab.lab._id, plateBarcode, this.popUpProtocolComponentName, plateType).subscribe({
			next: (result) => {
				this.blocked = false;
				const response: QCFailPlateDetailsResponse = result.data;
				const fetchedPlate: Plate = response.plateDetailDTO;
				if (!this.validateProjectAndWorkflow(fetchedPlate)) {
					this.popUpScanInputValues[scanIndex] = "";
					return;
				}
				const fetchedPlateProcessStepsMap: Map<string, ProcessStep> = this.prepareMapOfPlateProcessSteps(fetchedPlate);
				if (this.isRedoFromHistory || this.eligiblePlate(fetchedPlateProcessStepsMap, fetchedPlate)) {
					if (response.associatedArrays) {
						Object.entries(response.associatedArrays).forEach(([key, value]) => {
							this.associatedArrays[key] = value;
						});
					}
					if (response.mappedRedoPlate) {
						this.createRedoSelectionMap(response.mappedRedoPlate);
						this.mappedRedoPlates.push(response.mappedRedoPlate);	
					}
					if (response.scannedPlates) {
						Object.entries(response.scannedPlates).forEach(([key, value]) => {
							this.scannedPlatesMap[key] = value;
						});
					}
					this.popUpScanInputValues[scanIndex] = "";
				}
			},
			error: (_error) => {
				this.blocked = false;
				const message = this.errorHandler.handleError(_error);
				this.messagesService.addErrorMessage(message);
				this.popUpScanInputValues[scanIndex] = "";
			}
		});
	}

	createRedoSelectionMap(mappedRedoPlate: RedoPlateInformation) {
		if (mappedRedoPlate.sourcePlateInformations.length >= 1) {
			mappedRedoPlate.sourcePlateInformations.forEach(sourcePlateInformations => {
				if (sourcePlateInformations.sourcePlates.length >= 1) {
					sourcePlateInformations.sourcePlates.forEach(sourcePlate => {
						this.redoSelectionMap[sourcePlate] = "";
					})
				} else {
					this.redoSelectionMap[sourcePlateInformations.sourcePlateID] = "";
				}
			})
		} else {
			this.redoSelectionMap[mappedRedoPlate.currentPlateID] = "";
		}
	}

	eligiblePlate(processStepsMap: Map<string, ProcessStep>, fetchedPlate: Plate) {
		if ((!processStepsMap || processStepsMap === null)) {
			return false;
		}

		if (this.popUpUpdateType !== ButtonType.ACTION) {
			return false;
		}

		return this.activeProcessStepAction(this.popUpProtocolComponentName, this.popUpActionIndex, processStepsMap, fetchedPlate);
	}

	activeProcessStepAction(stepName: string, actionIndex: number, selectedPlateStepsMap: Map<string, ProcessStep>, fetchedPlate: Plate): boolean {
		let active = true;

		if (this.popUpProtocolComponentIndex > 0) {
			if (!selectedPlateStepsMap.has(this.selectedProtocol[this.openDialogDTO.stepListName][this.popUpProtocolComponentIndex - 1])) {
				this.showNotEligibleWarningMessage(ButtonType.ACTION, stepName);
				return false;
			}
			active = selectedPlateStepsMap.get(this.selectedProtocol[this.openDialogDTO.stepListName][this.popUpProtocolComponentIndex - 1])?.endTimeStamp !== null;
		}

		if (!selectedPlateStepsMap.has(stepName)) {
			this.showNotEligibleWarningMessage(ButtonType.ACTION, stepName);
			return false;
		}

		const protocolComponent: ProtocolComponent = this.selectedProtocol[this.openDialogDTO.stepMapName][this.selectedProtocol[this.openDialogDTO.stepListName][this.popUpProtocolComponentIndex]];
		const actionName = protocolComponent.actionList[actionIndex];

		if (selectedPlateStepsMap.get(stepName)?.actionMap.has(actionName)
			&& (fetchedPlate.status === BeadChipStatus.REDO || fetchedPlate.status === "NOT_PROCESSED")) {
			this.showNotEligibleWarningMessage(ButtonType.ACTION, stepName);
			return false
		}

		if (actionIndex > 0 && !selectedPlateStepsMap.get(stepName)?.actionMap.has(protocolComponent.actionList[actionIndex - 1])) {
			this.showNotEligibleWarningMessage(ButtonType.ACTION, stepName);
			return false
		}

		if (!active) {
			this.showNotEligibleWarningMessage(ButtonType.ACTION, stepName);
		}

		return active
	}

	showNotEligibleWarningMessage(updateType: string, stepName: string) {
		let errorDetail = `Plate is not eligible for ${stepName} - ${updateType}`;
		if (this.popUpActionIndex !== -1) {
			errorDetail += ` ${this.popUpAction.name}`
		}

		this.messagesService.addWarningMessage({
			severity: 'warn', summary: PLATE_NOT_ELIGIBLE,
			detail: errorDetail
		})
	}

	highlightNextScanInPopUpDialog() {
		this.redoSelectorDropdown.show(true)
	}
}
